import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  Bot, Plus, Zap, Newspaper, Twitter, TrendingUp, Clock, Play, Pause, 
  Trash2, Edit, History, AlertTriangle, CheckCircle2, XCircle, Settings
} from "lucide-react";
import type { TradingRule, TriggerRun, Signal } from "@shared/schema";
import { MainLayout } from "@/components/layout/main-layout";

function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

function RuleStatusBadge({ rule }: { rule: TradingRule }) {
  if (!rule.isEnabled) {
    return <Badge variant="secondary">Disabled</Badge>;
  }
  if (rule.paperTrade) {
    return <Badge variant="outline">Paper Trading</Badge>;
  }
  return <Badge variant="default">Active</Badge>;
}

function TriggerTypeBadge({ type }: { type: string }) {
  const icons: Record<string, React.ReactNode> = {
    news: <Newspaper className="h-3 w-3" />,
    social: <Twitter className="h-3 w-3" />,
    price: <TrendingUp className="h-3 w-3" />,
    custom: <Zap className="h-3 w-3" />,
  };
  return (
    <Badge variant="outline" className="gap-1">
      {icons[type] || <Zap className="h-3 w-3" />}
      {type}
    </Badge>
  );
}

function RunStatusBadge({ status }: { status: string }) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    executed: "default",
    pending: "secondary",
    evaluating: "secondary",
    failed: "destructive",
    skipped: "outline",
  };
  const icons: Record<string, React.ReactNode> = {
    executed: <CheckCircle2 className="h-3 w-3" />,
    pending: <Clock className="h-3 w-3" />,
    evaluating: <Settings className="h-3 w-3 animate-spin" />,
    failed: <XCircle className="h-3 w-3" />,
    skipped: <AlertTriangle className="h-3 w-3" />,
  };
  return (
    <Badge variant={variants[status] || "outline"} className="gap-1">
      {icons[status]}
      {status}
    </Badge>
  );
}

interface RuleFormData {
  name: string;
  description: string;
  triggerType: string;
  triggerSource: string;
  keywords: string;
  sentiment: string;
  assetClass: string;
  symbols: string;
  action: string;
  orderType: string;
  quantity: string;
  quantityType: string;
  maxPositionSize: string;
  dailyTradeLimit: string;
  paperTrade: boolean;
  activeHoursStart: string;
  activeHoursEnd: string;
  activeDays: string[];
}

const defaultFormData: RuleFormData = {
  name: "",
  description: "",
  triggerType: "news",
  triggerSource: "",
  keywords: "",
  sentiment: "positive",
  assetClass: "stock",
  symbols: "",
  action: "buy",
  orderType: "market",
  quantity: "100",
  quantityType: "shares",
  maxPositionSize: "10000",
  dailyTradeLimit: "5",
  paperTrade: true,
  activeHoursStart: "09:30",
  activeHoursEnd: "16:00",
  activeDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
};

function RuleBuilder({ 
  initialData, 
  onSubmit, 
  onCancel,
  isSubmitting 
}: { 
  initialData?: TradingRule;
  onSubmit: (data: RuleFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState<RuleFormData>(() => {
    if (initialData) {
      const conditions = initialData.conditions as Record<string, unknown> || {};
      return {
        name: initialData.name || "",
        description: initialData.description || "",
        triggerType: initialData.triggerType || "news",
        triggerSource: initialData.triggerSource || "",
        keywords: Array.isArray(conditions.keywords) ? conditions.keywords.join(", ") : "",
        sentiment: (conditions.sentiment as string) || "positive",
        assetClass: initialData.assetClass || "stock",
        symbols: initialData.symbols?.join(", ") || "",
        action: initialData.action || "buy",
        orderType: initialData.orderType || "market",
        quantity: initialData.quantity?.toString() || "100",
        quantityType: initialData.quantityType || "shares",
        maxPositionSize: initialData.maxPositionSize?.toString() || "10000",
        dailyTradeLimit: initialData.dailyTradeLimit?.toString() || "5",
        paperTrade: initialData.paperTrade ?? true,
        activeHoursStart: initialData.activeHoursStart || "09:30",
        activeHoursEnd: initialData.activeHoursEnd || "16:00",
        activeDays: initialData.activeDays || ["monday", "tuesday", "wednesday", "thursday", "friday"],
      };
    }
    return defaultFormData;
  });

  const [step, setStep] = useState(1);

  const updateField = <K extends keyof RuleFormData>(field: K, value: RuleFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day: string) => {
    setFormData(prev => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter(d => d !== day)
        : [...prev.activeDays, day],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        {[1, 2, 3, 4].map(s => (
          <div 
            key={s} 
            className={`flex-1 h-2 rounded-full ${s <= step ? "bg-primary" : "bg-muted"}`}
          />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Rule Details</h3>
          <div className="space-y-2">
            <Label htmlFor="name">Rule Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={e => updateField("name", e.target.value)}
              placeholder="e.g., Buy on positive earnings"
              data-testid="input-rule-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={e => updateField("description", e.target.value)}
              placeholder="Describe what this rule does..."
              data-testid="input-rule-description"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Trigger Conditions</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Trigger Type</Label>
              <Select value={formData.triggerType} onValueChange={v => updateField("triggerType", v)}>
                <SelectTrigger data-testid="select-trigger-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">News Event</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                  <SelectItem value="price">Price Alert</SelectItem>
                  <SelectItem value="custom">Custom Webhook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={formData.triggerSource} onValueChange={v => updateField("triggerSource", v)}>
                <SelectTrigger data-testid="select-trigger-source">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {formData.triggerType === "news" && (
                    <>
                      <SelectItem value="benzinga">Benzinga</SelectItem>
                      <SelectItem value="reuters">Reuters</SelectItem>
                      <SelectItem value="sec_filings">SEC Filings</SelectItem>
                    </>
                  )}
                  {formData.triggerType === "social" && (
                    <>
                      <SelectItem value="twitter">Twitter/X</SelectItem>
                      <SelectItem value="reddit">Reddit</SelectItem>
                      <SelectItem value="stocktwits">StockTwits</SelectItem>
                    </>
                  )}
                  {formData.triggerType === "price" && (
                    <>
                      <SelectItem value="price_above">Price Above</SelectItem>
                      <SelectItem value="price_below">Price Below</SelectItem>
                      <SelectItem value="volume_spike">Volume Spike</SelectItem>
                    </>
                  )}
                  {formData.triggerType === "custom" && (
                    <SelectItem value="webhook">Custom Webhook</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="keywords">Keywords (comma-separated)</Label>
            <Input
              id="keywords"
              value={formData.keywords}
              onChange={e => updateField("keywords", e.target.value)}
              placeholder="e.g., earnings beat, FDA approval, revenue growth"
              data-testid="input-keywords"
            />
          </div>
          <div className="space-y-2">
            <Label>Sentiment Filter</Label>
            <Select value={formData.sentiment} onValueChange={v => updateField("sentiment", v)}>
              <SelectTrigger data-testid="select-sentiment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="negative">Negative</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="any">Any</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Trading Action</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Asset Class</Label>
              <Select value={formData.assetClass} onValueChange={v => updateField("assetClass", v)}>
                <SelectTrigger data-testid="select-asset-class">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stock">Stocks</SelectItem>
                  <SelectItem value="option">Options</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={formData.action} onValueChange={v => updateField("action", v)}>
                <SelectTrigger data-testid="select-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">Buy</SelectItem>
                  <SelectItem value="sell">Sell</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="symbols">Symbols (comma-separated)</Label>
            <Input
              id="symbols"
              value={formData.symbols}
              onChange={e => updateField("symbols", e.target.value)}
              placeholder="e.g., AAPL, TSLA, NVDA"
              data-testid="input-symbols"
            />
            <p className="text-xs text-muted-foreground">Leave blank to trade the symbol mentioned in the signal</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Order Type</Label>
              <Select value={formData.orderType} onValueChange={v => updateField("orderType", v)}>
                <SelectTrigger data-testid="select-order-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="market">Market</SelectItem>
                  <SelectItem value="limit">Limit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                value={formData.quantity}
                onChange={e => updateField("quantity", e.target.value)}
                data-testid="input-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Quantity Type</Label>
              <Select value={formData.quantityType} onValueChange={v => updateField("quantityType", v)}>
                <SelectTrigger data-testid="select-quantity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shares">Shares</SelectItem>
                  <SelectItem value="dollars">Dollars</SelectItem>
                  <SelectItem value="percent">% of Portfolio</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Risk Controls & Schedule</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxPositionSize">Max Position Size ($)</Label>
              <Input
                id="maxPositionSize"
                type="number"
                value={formData.maxPositionSize}
                onChange={e => updateField("maxPositionSize", e.target.value)}
                data-testid="input-max-position"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dailyTradeLimit">Daily Trade Limit</Label>
              <Input
                id="dailyTradeLimit"
                type="number"
                value={formData.dailyTradeLimit}
                onChange={e => updateField("dailyTradeLimit", e.target.value)}
                data-testid="input-daily-limit"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="space-y-0.5">
              <Label>Paper Trading Mode</Label>
              <p className="text-sm text-muted-foreground">Simulate trades without real money</p>
            </div>
            <Switch
              checked={formData.paperTrade}
              onCheckedChange={v => updateField("paperTrade", v)}
              data-testid="switch-paper-trade"
            />
          </div>

          {!formData.paperTrade && (
            <div className="p-4 rounded-lg border border-destructive bg-destructive/10">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-semibold">Live Trading Warning</span>
              </div>
              <p className="text-sm mt-1">Real orders will be placed. Ensure you understand the risks.</p>
            </div>
          )}

          <Separator />

          <div className="space-y-4">
            <Label>Trading Schedule</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="activeHoursStart" className="text-sm">Start Time</Label>
                <Input
                  id="activeHoursStart"
                  type="time"
                  value={formData.activeHoursStart}
                  onChange={e => updateField("activeHoursStart", e.target.value)}
                  data-testid="input-hours-start"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="activeHoursEnd" className="text-sm">End Time</Label>
                <Input
                  id="activeHoursEnd"
                  type="time"
                  value={formData.activeHoursEnd}
                  onChange={e => updateField("activeHoursEnd", e.target.value)}
                  data-testid="input-hours-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Active Days</Label>
              <div className="flex flex-wrap gap-2">
                {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map(day => (
                  <Badge
                    key={day}
                    variant={formData.activeDays.includes(day) ? "default" : "outline"}
                    className="cursor-pointer capitalize"
                    onClick={() => toggleDay(day)}
                    data-testid={`badge-day-${day}`}
                  >
                    {day.slice(0, 3)}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={step === 1 ? onCancel : () => setStep(step - 1)} data-testid="button-back">
          {step === 1 ? "Cancel" : "Back"}
        </Button>
        {step < 4 ? (
          <Button onClick={() => setStep(step + 1)} disabled={step === 1 && !formData.name} data-testid="button-next">
            Next
          </Button>
        ) : (
          <Button onClick={() => onSubmit(formData)} disabled={isSubmitting} data-testid="button-save-rule">
            {isSubmitting ? "Saving..." : (initialData ? "Update Rule" : "Create Rule")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function TradingAutomation() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("rules");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<TradingRule | null>(null);

  const { data: rules, isLoading: rulesLoading } = useQuery<TradingRule[]>({
    queryKey: ["/api/trading-rules"],
  });

  const { data: triggerRuns, isLoading: runsLoading } = useQuery<TriggerRun[]>({
    queryKey: ["/api/trigger-runs"],
    refetchInterval: 5000,
  });

  const { data: signals, isLoading: signalsLoading } = useQuery<Signal[]>({
    queryKey: ["/api/signals"],
    refetchInterval: 5000,
  });

  const createRuleMutation = useMutation({
    mutationFn: async (data: RuleFormData) => {
      const payload = {
        name: data.name,
        description: data.description,
        triggerType: data.triggerType,
        triggerSource: data.triggerSource || null,
        conditions: {
          keywords: data.keywords.split(",").map(k => k.trim()).filter(Boolean),
          sentiment: data.sentiment,
        },
        assetClass: data.assetClass,
        symbols: data.symbols.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
        action: data.action,
        orderType: data.orderType,
        quantity: data.quantity,
        quantityType: data.quantityType,
        maxPositionSize: data.maxPositionSize,
        dailyTradeLimit: parseInt(data.dailyTradeLimit),
        paperTrade: data.paperTrade,
        activeHoursStart: data.activeHoursStart,
        activeHoursEnd: data.activeHoursEnd,
        activeDays: data.activeDays,
        isEnabled: true,
      };
      return apiRequest("POST", "/api/trading-rules", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/trading-rules"] });
      toast({ title: "Rule created successfully" });
      setIsDialogOpen(false);
    },
    onError: () => {
      toast({ title: "Failed to create rule", variant: "destructive" });
    },
  });

  const updateRuleMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TradingRule> }) => {
      return apiRequest("PATCH", `/api/trading-rules/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/trading-rules"] });
      toast({ title: "Rule updated" });
      setIsDialogOpen(false);
      setEditingRule(null);
    },
    onError: () => {
      toast({ title: "Failed to update rule", variant: "destructive" });
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/trading-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/trading-rules"] });
      toast({ title: "Rule deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete rule", variant: "destructive" });
    },
  });

  const toggleRuleMutation = useMutation({
    mutationFn: async ({ id, isEnabled }: { id: string; isEnabled: boolean }) => {
      return apiRequest("PATCH", `/api/trading-rules/${id}`, { isEnabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/trading-rules"] });
    },
  });

  const handleSubmit = (data: RuleFormData) => {
    if (editingRule) {
      const payload = {
        name: data.name,
        description: data.description,
        triggerType: data.triggerType,
        triggerSource: data.triggerSource || null,
        conditions: {
          keywords: data.keywords.split(",").map(k => k.trim()).filter(Boolean),
          sentiment: data.sentiment,
        },
        assetClass: data.assetClass,
        symbols: data.symbols.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
        action: data.action,
        orderType: data.orderType,
        quantity: data.quantity,
        quantityType: data.quantityType,
        maxPositionSize: data.maxPositionSize,
        dailyTradeLimit: parseInt(data.dailyTradeLimit),
        paperTrade: data.paperTrade,
        activeHoursStart: data.activeHoursStart,
        activeHoursEnd: data.activeHoursEnd,
        activeDays: data.activeDays,
      };
      updateRuleMutation.mutate({ id: editingRule.id, data: payload as Partial<TradingRule> });
    } else {
      createRuleMutation.mutate(data);
    }
  };

  const openEditDialog = (rule: TradingRule) => {
    setEditingRule(rule);
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingRule(null);
    setIsDialogOpen(true);
  };

  return (
    <MainLayout
      title="Trading Automation"
      subtitle="Create automated trading rules triggered by news, social media, and custom signals"
      actions={
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="button-new-rule">
              <Plus className="h-4 w-4 mr-2" />
              New Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRule ? "Edit Trading Rule" : "Create Trading Rule"}</DialogTitle>
              <DialogDescription>
                Configure triggers, conditions, and actions for automated trading
              </DialogDescription>
            </DialogHeader>
            <RuleBuilder
              initialData={editingRule || undefined}
              onSubmit={handleSubmit}
              onCancel={() => {
                setIsDialogOpen(false);
                setEditingRule(null);
              }}
              isSubmitting={createRuleMutation.isPending || updateRuleMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Zap className="h-4 w-4 mr-2" />
            Rules ({rules?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-4 w-4 mr-2" />
            Execution History
          </TabsTrigger>
          <TabsTrigger value="signals" data-testid="tab-signals">
            <Newspaper className="h-4 w-4 mr-2" />
            Signals Feed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-6">
          {rulesLoading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Loading rules...
              </CardContent>
            </Card>
          ) : !rules?.length ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Trading Rules</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first automated trading rule to get started
                </p>
                <Button onClick={openCreateDialog} data-testid="button-create-first-rule">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {rules.map(rule => (
                <Card key={rule.id} data-testid={`rule-card-${rule.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-lg">{rule.name}</CardTitle>
                        <RuleStatusBadge rule={rule} />
                        <TriggerTypeBadge type={rule.triggerType} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.isEnabled ?? false}
                          onCheckedChange={isEnabled => toggleRuleMutation.mutate({ id: rule.id, isEnabled })}
                          data-testid={`switch-enable-${rule.id}`}
                        />
                        <Button size="icon" variant="ghost" onClick={() => openEditDialog(rule)} data-testid={`button-edit-${rule.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => deleteRuleMutation.mutate(rule.id)}
                          data-testid={`button-delete-${rule.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {rule.description && (
                      <CardDescription>{rule.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Action:</span>
                        <p className="font-medium capitalize">{rule.action} {rule.assetClass}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Symbols:</span>
                        <p className="font-medium">{rule.symbols?.join(", ") || "From signal"}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Quantity:</span>
                        <p className="font-medium">{rule.quantity} {rule.quantityType}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Executions:</span>
                        <p className="font-medium">{rule.totalExecutions || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="text-xs text-muted-foreground border-t pt-3">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span>Created: {formatDate(rule.createdAt)}</span>
                      {rule.lastTriggeredAt && <span>Last triggered: {formatDate(rule.lastTriggeredAt)}</span>}
                      {rule.activeHoursStart && rule.activeHoursEnd && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {rule.activeHoursStart} - {rule.activeHoursEnd}
                        </span>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
              <CardDescription>View all automated trade executions and their results</CardDescription>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <p className="text-muted-foreground text-center py-8">Loading history...</p>
              ) : !triggerRuns?.length ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No executions yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    When your rules trigger trades, they'll appear here
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Rule</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {triggerRuns.map(run => {
                      const rule = rules?.find(r => r.id === run.ruleId);
                      return (
                        <TableRow key={run.id} data-testid={`run-row-${run.id}`}>
                          <TableCell className="text-sm">{formatDate(run.createdAt)}</TableCell>
                          <TableCell>{rule?.name || "Unknown Rule"}</TableCell>
                          <TableCell><RunStatusBadge status={run.status} /></TableCell>
                          <TableCell className="font-mono">{run.executedSymbol || "—"}</TableCell>
                          <TableCell>{run.executedQuantity?.toString() || "—"}</TableCell>
                          <TableCell>{run.executedPrice ? `$${run.executedPrice}` : "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                            {run.errorMessage || (run.conditionsMatched ? "Matched" : "—")}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="signals" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Signals Feed</CardTitle>
              <CardDescription>Incoming signals from news and social media sources</CardDescription>
            </CardHeader>
            <CardContent>
              {signalsLoading ? (
                <p className="text-muted-foreground text-center py-8">Loading signals...</p>
              ) : !signals?.length ? (
                <div className="text-center py-12">
                  <Newspaper className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No signals received yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure signal sources to start receiving events
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {signals.map(signal => (
                    <div key={signal.id} className="p-4 border rounded-lg" data-testid={`signal-${signal.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{signal.eventType}</Badge>
                            {signal.sentiment && (
                              <Badge variant={signal.sentiment === "positive" ? "default" : signal.sentiment === "negative" ? "destructive" : "secondary"}>
                                {signal.sentiment}
                              </Badge>
                            )}
                            {signal.symbols?.map(sym => (
                              <Badge key={sym} variant="outline" className="font-mono">{sym}</Badge>
                            ))}
                          </div>
                          {signal.headline && <p className="font-medium">{signal.headline}</p>}
                          {signal.content && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{signal.content}</p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatDate(signal.publishedAt || signal.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </MainLayout>
  );
}
